// Inline staff sign-in form. Renders directly on the page (no portal /
// overlay) — the surrounding page provides framing.
//
// Used by /:slug/admin/* via PublisherAdminGate and by /onboard via
// OnboardChannelPage. Advertiser-facing surfaces (PublicHeader,
// /my-campaigns) use the OTP-only SignInModal instead.
//
// Stages:
//   • password    — default. Email + password + Sign in. Below: "Forgot
//                   password?" and "Or send me a one-time email code" links.
//   • otp-email   — email-only field + Send code. Reachable from the
//                   "send me a code" link in the password stage.
//   • otp-code    — 6-digit code field + Verify. Reached after the email
//                   stage submits successfully.
//   • forgot-sent — confirmation that a password-reset email was sent.
//                   Reached from "Forgot password?" in the password stage.

import React from 'react';
import { useParams } from 'react-router-dom';
import { Mail, KeyRound, Lock, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from './useAuth';
import { selfServiceSupabase } from '../../../lib/selfServiceSupabase';
import { apiClient } from '../../../lib/apiClient';
import PrimaryButton from '../shared/PrimaryButton';
import { CTV_CTA_ACCENT, CTV_CTA_DEEP } from './CtvBuyerAuthShell';

interface StaffSignInFormProps {
  initialEmail?: string;
  /** Where Supabase should redirect after a password-reset email click. Defaults to `${origin}/reset-password`. */
  resetRedirectTo?: string;
}

type Stage = 'password' | 'otp-email' | 'otp-code' | 'forgot-sent';

const StaffSignInForm: React.FC<StaffSignInFormProps> = ({
  initialEmail = '',
  resetRedirectTo,
}) => {
  const { requestOtp, verifyOtp } = useAuth();
  // Pull the current station slug from the route (e.g. /:slug/admin/*) so
  // auth-email branding matches the station the staff user is signing into.
  const { slug } = useParams<{ slug: string }>();
  const [stage, setStage] = React.useState<Stage>('password');
  const [email, setEmail] = React.useState(initialEmail);
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [devCode, setDevCode] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [showOtpHint, setShowOtpHint] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const emailRef = React.useRef<HTMLInputElement | null>(null);

  const switchToOtp = async () => {
    setError(null);
    setShowOtpHint(false);
    if (!email.includes('@')) {
      setStage('otp-email');
      return;
    }
    setBusy(true);
    try {
      const { devCode: dev } = await requestOtp(email, slug);
      setDevCode(dev);
      setCode('');
      setStage('otp-code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
      setStage('otp-email');
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selfServiceSupabase) {
      setError('Supabase Auth is not configured.');
      return;
    }
    if (!email.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    if (password.length < 1) {
      setError('Enter your password');
      return;
    }
    setError(null);
    setShowOtpHint(false);
    setBusy(true);
    try {
      const { error: authErr } = await selfServiceSupabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authErr) throw authErr;
      // onAuthStateChange in useAuth will pick up the new session; the
      // surrounding gate component re-renders into the signed-in state.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
      setShowOtpHint(true);
    } finally {
      setBusy(false);
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { devCode: dev } = await requestOtp(email, slug);
      setDevCode(dev);
      setStage('otp-code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await verifyOtp(email, code);
      // Session change propagates via useAuth's onAuthStateChange listener.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.includes('@')) {
      setError('Enter your email first, then click "Forgot password?".');
      emailRef.current?.focus();
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const redirectTo =
        resetRedirectTo ?? `${window.location.origin}/reset-password`;
      // ADR 0001: backend mints the Supabase recovery token via
      // admin.generateLink, then dispatches our SendGrid template — we no
      // longer call resetPasswordForEmail (Supabase's built-in mailer).
      await apiClient.post(
        '/auth/password-reset/request',
        { email, stationSlug: slug, redirectTo },
        { anonymous: true },
      );
      setStage('forgot-sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'forgot-sent') {
    return (
      <div className="space-y-4 text-left">
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-4">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold">Check your email</div>
            <p className="mt-1">
              We sent a password-reset link to{' '}
              <span className="font-medium">{email}</span>. Click it to set a new
              password.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setStage('password');
            setError(null);
          }}
          className="w-full py-2 text-sm text-slate-600 hover:text-slate-900"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  if (stage === 'otp-email') {
    return (
      <form onSubmit={handleSendOtp} className="space-y-4 text-left">
        <p className="text-sm text-slate-600">
          We'll email you a 6-digit code to sign in.
        </p>
        <label className="block">
          <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
            Email
          </span>
          <div className="mt-1 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@station.com"
              className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#7e80ff] focus:border-[#7e80ff] outline-none"
            />
          </div>
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setStage('password');
              setError(null);
            }}
            className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
          >
            Back
          </button>
          <PrimaryButton
            type="submit"
            disabled={busy}
            accentColor={CTV_CTA_ACCENT}
            primaryColor={CTV_CTA_DEEP}
            className="flex-1"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Send code
          </PrimaryButton>
        </div>
      </form>
    );
  }

  if (stage === 'otp-code') {
    return (
      <form onSubmit={handleVerifyOtp} className="space-y-4 text-left">
        <p className="text-sm text-slate-600">
          We sent a code to <span className="font-medium text-slate-900">{email}</span>.
        </p>
        {devCode && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
            <div className="font-semibold uppercase tracking-wide mb-1">Demo mode</div>
            Your code is{' '}
            <span className="font-mono font-bold text-base">{devCode}</span>. Paste it
            below.
          </div>
        )}
        <label className="block">
          <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
            6-digit code
          </span>
          <div className="mt-1 relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="000000"
              className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm font-mono tracking-widest focus:ring-2 focus:ring-[#7e80ff] focus:border-[#7e80ff] outline-none"
            />
          </div>
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setStage('password');
              setCode('');
              setError(null);
            }}
            className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
          >
            Back
          </button>
          <PrimaryButton
            type="submit"
            disabled={busy}
            accentColor={CTV_CTA_ACCENT}
            primaryColor={CTV_CTA_DEEP}
            className="flex-1"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Verify &amp; Sign In
          </PrimaryButton>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handlePasswordSignIn} className="space-y-4 text-left">
      <label className="block">
        <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
          Email
        </span>
        <div className="mt-1 relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={emailRef}
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@station.com"
            className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#7e80ff] focus:border-[#7e80ff] outline-none"
          />
        </div>
      </label>
      <label className="block">
        <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
          Password
        </span>
        <div className="mt-1 relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#7e80ff] focus:border-[#7e80ff] outline-none"
          />
        </div>
      </label>
      {error && (
        <div className="flex items-start gap-2 text-xs text-red-600">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            {error}
            {showOtpHint && (
              <>
                {' '}—{' '}
                <button
                  type="button"
                  onClick={switchToOtp}
                  className="underline font-medium hover:text-red-700"
                >
                  try email code instead
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <PrimaryButton
        type="submit"
        disabled={busy}
        accentColor={CTV_CTA_ACCENT}
        primaryColor={CTV_CTA_DEEP}
        fullWidth
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        Sign In
      </PrimaryButton>
      <div className="flex items-center justify-between text-xs text-slate-600">
        <button
          type="button"
          onClick={handleForgotPassword}
          disabled={busy}
          className="hover:text-slate-900 hover:underline disabled:opacity-50"
        >
          Forgot password?
        </button>
        <button
          type="button"
          onClick={switchToOtp}
          disabled={busy}
          className="hover:text-slate-900 hover:underline disabled:opacity-50"
        >
          Or send me a one-time email code
        </button>
      </div>
    </form>
  );
};

export default StaffSignInForm;
